import TIM from 'tim-js-sdk';
import TIMUploadPlugin from 'tim-upload-plugin';
import { CS_EMOJI_PACK_INDEX, resolveFaceDisplay, toFacePayloadData } from '@/constants/emoji';
import { normalizeDisplayMessage } from '@/utils/messages';
let tim = null;
let messageHandler = null;
let refreshCredential = null;
let selfImUid = '';
let sdkReady = false;
let sdkReadyWaiters = [];
function normalizeMessageList(raw) {
    if (Array.isArray(raw))
        return raw;
    if (raw && typeof raw === 'object')
        return [raw];
    return [];
}
function pickImageInfoUrl(item) {
    if (!item)
        return '';
    return (item.url || item.imageUrl || item.URL || '').trim();
}
function getImageUrl(payload) {
    if (!payload)
        return '';
    const direct = pickImageInfoUrl(payload);
    if (direct)
        return direct;
    const arr = Array.isArray(payload.imageInfoArray) ? payload.imageInfoArray : [];
    for (const type of [2, 0, 1]) {
        const matched = arr.find((item) => item.type === type);
        const url = pickImageInfoUrl(matched);
        if (url)
            return url;
    }
    for (const item of arr) {
        const url = pickImageInfoUrl(item);
        if (url)
            return url;
    }
    return '';
}
function mapTimMessage(message) {
    if (!message?.ID || !message.from || !message.to || !message.type)
        return null;
    const payload = message.payload ?? {};
    const base = {
        msgId: message.ID,
        fromImUid: message.from,
        toImUid: message.to,
        msgType: message.type,
        msgTime: (message.time ?? 0) * 1000,
        fromSelf: message.from === selfImUid,
    };
    if (message.type === TIM.TYPES.MSG_TEXT) {
        return {
            ...base,
            kind: 'text',
            content: payload.text ?? '',
        };
    }
    if (message.type === TIM.TYPES.MSG_FACE) {
        const faceData = typeof payload.data === 'string' ? payload.data : String(payload.data ?? '');
        const face = resolveFaceDisplay(payload.index, faceData);
        return {
            ...base,
            kind: 'face',
            faceIndex: payload.index,
            faceData,
            imageUrl: face.imageUrl,
            content: face.imageUrl ? '[表情]' : '[表情]',
        };
    }
    if (message.type === TIM.TYPES.MSG_IMAGE) {
        return normalizeDisplayMessage({
            ...base,
            kind: 'image',
            imageUrl: getImageUrl(payload),
            content: '[图片]',
        });
    }
    return null;
}
function markSdkReady() {
    sdkReady = true;
    sdkReadyWaiters.forEach((resolve) => resolve());
    sdkReadyWaiters = [];
}
function markSdkNotReady() {
    sdkReady = false;
}
function waitForSdkReady(timeoutMs = 15000) {
    if (sdkReady)
        return Promise.resolve();
    return new Promise((resolve, reject) => {
        const timer = window.setTimeout(() => {
            reject(new Error('IM SDK 初始化超时，请稍后重试'));
        }, timeoutMs);
        sdkReadyWaiters.push(() => {
            window.clearTimeout(timer);
            resolve();
        });
    });
}
function bindTimEvents(instance, loginUserId) {
    instance.on(TIM.EVENT.SDK_READY, () => {
        markSdkReady();
    });
    instance.on(TIM.EVENT.SDK_NOT_READY, () => {
        markSdkNotReady();
    });
    instance.on(TIM.EVENT.MESSAGE_RECEIVED, (event) => {
        if (!messageHandler)
            return;
        for (const item of normalizeMessageList(event?.data)) {
            const mapped = mapTimMessage(item);
            if (mapped)
                messageHandler(mapped);
        }
    });
    instance.on(TIM.EVENT.ERROR, async (event) => {
        const data = event.data;
        if (data?.code === 70001 && refreshCredential && tim) {
            markSdkNotReady();
            const cred = await refreshCredential();
            await tim.login({ userID: loginUserId, userSig: cred.userSig });
            await waitForSdkReady();
        }
    });
}
export async function loginTim(sdkAppId, userId, userSig, onRefresh) {
    selfImUid = userId;
    refreshCredential = onRefresh ?? null;
    if (!tim) {
        tim = TIM.create({ SDKAppID: sdkAppId });
        tim.registerPlugin({ 'tim-upload-plugin': TIMUploadPlugin });
        bindTimEvents(tim, userId);
    }
    markSdkNotReady();
    const readyPromise = waitForSdkReady();
    await tim.login({ userID: userId, userSig });
    await readyPromise;
}
export function onTimMessage(handler) {
    messageHandler = handler;
}
function isSupportedHistoryMessage(message) {
    return (message.type === TIM.TYPES.MSG_TEXT ||
        message.type === TIM.TYPES.MSG_FACE ||
        message.type === TIM.TYPES.MSG_IMAGE);
}
/** 拉取 C2C 会话腾讯 IM 云端漫游消息（需 SDK ready 后调用） */
export async function fetchC2CHistory(peerId, maxCount = 100) {
    if (!tim || !peerId)
        return [];
    await waitForSdkReady();
    const conversationID = `C2C${peerId}`;
    const allMessages = [];
    let nextReqMessageID = '';
    while (allMessages.length < maxCount) {
        const res = await tim.getMessageList({
            conversationID,
            nextReqMessageID,
            count: 15,
        });
        const payload = (res?.data ?? {});
        const list = Array.isArray(payload.messageList) ? payload.messageList : [];
        for (const item of list) {
            if (!item || !isSupportedHistoryMessage(item))
                continue;
            const mapped = mapTimMessage(item);
            if (mapped)
                allMessages.push(mapped);
            if (allMessages.length >= maxCount)
                break;
        }
        if (payload.isCompleted || !payload.nextReqMessageID || list.length === 0)
            break;
        nextReqMessageID = payload.nextReqMessageID;
    }
    return allMessages;
}
function extractSentMessage(res) {
    const message = res?.data?.message;
    return {
        msgId: message?.ID,
        imageUrl: message?.type === TIM.TYPES.MSG_IMAGE ? getImageUrl(message.payload) : '',
    };
}
export async function sendText(peerId, text) {
    if (!tim)
        throw new Error('IM 未初始化');
    await waitForSdkReady();
    const message = tim.createTextMessage({
        to: peerId,
        conversationType: TIM.TYPES.CONV_C2C,
        payload: { text },
    });
    const res = await tim.sendMessage(message);
    return extractSentMessage(res).msgId;
}
export async function sendFace(peerId, data, index = CS_EMOJI_PACK_INDEX) {
    if (!tim)
        throw new Error('IM 未初始化');
    await waitForSdkReady();
    const message = tim.createFaceMessage({
        to: peerId,
        conversationType: TIM.TYPES.CONV_C2C,
        payload: { index, data: toFacePayloadData(data) },
    });
    const res = await tim.sendMessage(message);
    return extractSentMessage(res).msgId;
}
export async function sendImage(peerId, file) {
    if (!tim)
        throw new Error('IM 未初始化');
    if (!file)
        throw new Error('图片无效');
    await waitForSdkReady();
    const message = tim.createImageMessage({
        to: peerId,
        conversationType: TIM.TYPES.CONV_C2C,
        payload: { file },
    });
    const res = await tim.sendMessage(message);
    return extractSentMessage(res);
}
export function destroyTim() {
    if (tim) {
        tim.destroy();
        tim = null;
    }
    messageHandler = null;
    refreshCredential = null;
    selfImUid = '';
    sdkReady = false;
    sdkReadyWaiters = [];
}
